import type { ProgrammaticLandingPageConfig } from '../../types'
import { KWP_SIZE_FACTS } from '../kwpSizeFacts'

const FACTS = KWP_SIZE_FACTS[10]

export const KWP_10_INSTALACION_FOTOVOLTAICA: ProgrammaticLandingPageConfig = {
  slug: 'instalacion-fotovoltaica-10-kwp-precio',
  lang: 'es',
  topicId: 'kwp-kosten-10',
  title: 'Instalación fotovoltaica de 10 kWp: precio y valores orientativos',
  metaDescription:
    '¿Cuánto cuesta una instalación fotovoltaica de 10 kWp? Superficie de tejado, número de ' +
    'paneles, desglose de costes y comparación gratuita para el tamaño familiar más habitual.',
  keywords: [
    'instalación fotovoltaica 10 kWp precio',
    'placas solares 10 kWp coste',
    'cuánto cuesta una instalación de 10 kWp',
    '10 kWp instalación solar hogar',
  ],
  eyebrow: 'Tamaño de instalación: 10 kWp',
  h1: 'Instalación fotovoltaica de 10 kWp: precio y valores orientativos',
  intro:
    '10 kWp es uno de los tamaños más elegidos para viviendas unifamiliares en Alemania - entre ' +
    'otras razones porque hasta ese límite se aplica una retribución por excedentes específica en ' +
    'el sistema alemán (EEG). Aquí encontrará valores orientativos sobre superficie de tejado y ' +
    'número de paneles, un desglose de costes y justo debajo una calculadora con el precio medio ' +
    'para 10 kWp ya calculado a partir de presupuestos reales de empresas instaladoras.',
  calculatorPrefill: { kind: 'kwp', kwp: 10 },
  indexPolicy: 'index',
  sections: [
    { type: 'heading', text: '¿Para quién es adecuada una instalación de 10 kWp?' },
    {
      type: 'paragraph',
      text:
        'Valor orientativo: 10 kWp suele encajar con un hogar de 4-5 personas o una vivienda ' +
        'unifamiliar clásica con un consumo anual de unos 5.000-7.000 kWh - a menudo combinada ' +
        'con una batería para aumentar el autoconsumo.',
    },
    { type: 'heading', text: 'Tejado y paneles: lo que necesita una instalación de 10 kWp' },
    {
      type: 'paragraph',
      text:
        `Valor orientativo: unos ${FACTS.roofAreaM2} m² de tejado y aproximadamente ` +
        `${FACTS.moduleCount} paneles con módulos habituales de ~440 Wp. La producción anual, con ` +
        `orientación sur y sin sombras, ronda los ${FACTS.annualYieldKwh.toLocaleString('es-ES')} kWh. ` +
        'Un cálculo exacto para su tejado lo ofrece la calculadora de dimensionamiento.',
    },
    { type: 'heading', text: 'Componentes del coste de una instalación fotovoltaica' },
    {
      type: 'list',
      items: [
        'Paneles: el mayor coste individual, normalmente en torno a un tercio del total',
        'Inversor: con 10 kWp, monofásico o trifásico según la acometida de la vivienda',
        'Montaje/mano de obra: fijación en el tejado, cableado',
        'Andamiaje: depende de la altura y accesibilidad del tejado',
        'Instalación eléctrica: adaptación del cuadro eléctrico, conexión a la red de la vivienda',
        'Tramitación: registro ante la distribuidora y organismos correspondientes',
      ],
    },
    {
      type: 'paragraph',
      text:
        '10 kWp es además un límite legalmente relevante en Alemania: hasta ese tamaño se aplica ' +
        'una retribución por excedentes (EEG) más alta - para el cálculo del precio de la ' +
        'instalación en sí, sin embargo, lo que sigue contando es el precio por kWp.',
    },
    { type: 'heading', text: 'Amortización y autoconsumo' },
    {
      type: 'paragraph',
      text:
        'Con una batería bien dimensionada, el autoconsumo de una instalación de 10 kWp puede ' +
        'superar claramente el valor sin batería (normalmente en torno al 25-30 %). En Alemania, ' +
        'hasta 10 kWp se aplica actualmente una retribución de 7,78 ct/kWh para la parte de ' +
        'excedente (dato de febrero de 2026, baja a 7,71 ct/kWh el 1 de agosto de 2026). Un cálculo ' +
        'exacto para su tejado lo ofrece la calculadora de dimensionamiento.',
    },
  ],
  faq: [
    {
      q: '¿Por qué es 10 kWp un tamaño de instalación tan habitual?',
      a: 'Por un lado encaja con el consumo típico de una vivienda unifamiliar, y por otro, en Alemania se aplica hasta 10 kWp una retribución por excedentes (EEG) más alta que por encima de ese límite.',
    },
    {
      q: '¿Cambia algo en la retribución por excedentes a partir de 10 kWp?',
      a: 'Sí, por encima de 10 kWp se aplica otra tarifa para la electricidad vertida a la red en el sistema alemán. Para la comparación del precio de la instalación en sí, esto es independiente de la retribución.',
    },
    {
      q: '¿Es suficiente 10 kWp para una vivienda unifamiliar con batería?',
      a: 'En la mayoría de los casos sí - 10 kWp es uno de los tamaños estándar para viviendas unifamiliares con batería. Con bomba de calor o coche eléctrico adicionales, conviene valorar 12 kWp o más.',
    },
  ],
  primaryCta: { label: 'Solicitar presupuesto gratis ahora', action: 'customer' },
  secondaryCta: {
    label: 'Coste de una instalación fotovoltaica: guía completa',
    action: 'link',
    href: '/coste-instalacion-fotovoltaica',
  },
  relatedLinks: [
    { label: 'Instalación fotovoltaica de 8 kWp: precio y valores orientativos', href: '/instalacion-fotovoltaica-8-kwp-precio' },
    { label: 'Instalación fotovoltaica de 12 kWp: precio y valores orientativos', href: '/instalacion-fotovoltaica-12-kwp-precio' },
    { label: 'Instalación fotovoltaica de 15 kWp: precio y valores orientativos', href: '/instalacion-fotovoltaica-15-kwp-precio' },
    { label: 'Batería solar de 10 kWh: precio y valores orientativos', href: '/bateria-solar-10-kwh-precio' },
    { label: 'Cómo funciona la comparación de presupuestos', href: '/comparar-presupuestos-fotovoltaicos' },
  ],
}
